import React, { useEffect, useState } from "react";
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Typography,
  Button,
  Box,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  Avatar,
} from "@mui/material";
import {
  DeleteForever,
  PersonOff,
  Email,
  CalendarToday,
} from "@mui/icons-material";
import { getMarkedForDeletion, deleteAccountPermanently } from "../../context/User";

const DeletionRequestsComponent = () => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [open, setOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [deletionData, setDeletionData] = useState(null);
  const [showDeletionModal, setShowDeletionModal] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getMarkedForDeletion();
      setData(result);
    } catch (error) {
      console.error("Error fetching deletion requests:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, 15000);
    return () => clearInterval(intervalId);
  }, []);

  const formatDate = (createdAt) => {
    try {
      const date = new Date(createdAt);
      if (isNaN(date.getTime())) throw new Error("Invalid timestamp");
      return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    } catch {
      return "Invalid date";
    }
  };

  const handleDeleteRequest = (user) => {
    setSelectedUser(user);
    setOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser) return;

    try {
      setActionLoading(true);
      const response = await deleteAccountPermanently(selectedUser._id || selectedUser.id, true);
      setDeletionData(response);
      setSuccessMessage(`Account for ${selectedUser.name || selectedUser.email} has been permanently deleted.`);
      await fetchData();
      setOpen(false);
      setShowDeletionModal(true);
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      setError(error.message);
      setOpen(false);
    } finally {
      setActionLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUser(null);
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 4,
        px: { xs: 2, sm: 3 },
        backgroundColor: theme.palette.background.default,
        minHeight: "100vh",
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        mb={4}
        color={theme.palette.text.primary}
      >
        Deletion Requests
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", height: "50vh" }}>
          <CircularProgress color="secondary" />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : data.length === 0 ? (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            mt: 2,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography variant="h6" color="textSecondary">
            There are no pending deletion requests at this time
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: theme.palette.background.paper,
            boxShadow: 3,
            borderRadius: 2,
          }}
        >
          <Table>
            <TableHead sx={{ backgroundColor: theme.palette.action.hover }}>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>User</TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>Request Date</TableCell>
                <TableCell sx={{ fontWeight: "bold", fontSize: 16 }}>Status</TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", fontSize: 16 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((user) => (
                <TableRow
                  key={user._id || user.id}
                  sx={{
                    "&:hover": { backgroundColor: theme.palette.action.hover },
                    transition: "background-color 0.2s",
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar
                        src={user.profilePicture || user.picture}
                        alt={user.username}
                        sx={{ width: 40, height: 40 }}
                      >
                        {(user.name || user.email)?.[0]?.toUpperCase()}
                      </Avatar>
                      <Typography variant="body1" fontWeight={500} p={0} m={0}>
                        {user.username || "N/A"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Email fontSize="small" color="action" />
                      <Typography variant="body2"  p={0} m={0}>
                        {user.email || "N/A"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CalendarToday fontSize="small" color="action" />
                      <Typography variant="body2"  p={0} m={0}>
                        {formatDate(user.markedForDeletionAt || user.createdAt)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<PersonOff />}
                      label="Pending Deletion"
                      color="warning"
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<DeleteForever />}
                      onClick={() => handleDeleteRequest(user)}
                      size="small"
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        px: 2,
                      }}
                    >
                      Delete Permanently
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", fontSize: 20 }}>
          Confirm Permanent Deletion
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          {selectedUser && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to permanently delete this account?
              </Typography>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: theme.palette.action.hover,
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" gutterBottom>
                  <strong>Name:</strong> {selectedUser.name || "N/A"}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Email:</strong> {selectedUser.email || "N/A"}
                </Typography>
                <Typography variant="body2">
                  <strong>User ID:</strong> {selectedUser._id || selectedUser.id}
                </Typography>
              </Box>
              <Alert severity="error" sx={{ mt: 2 }}>
                This action cannot be undone. All user data will be permanently removed from the system.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={actionLoading}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <DeleteForever />}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {actionLoading ? "Deleting..." : "Delete Permanently"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deletion Data Modal */}
      <Dialog
        open={showDeletionModal}
        onClose={() => setShowDeletionModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", fontSize: 20 }}>
          Deletion Summary
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          {deletionData && (
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                Account has been successfully deleted from the system.
              </Alert>
              
              <Typography variant="h6" fontWeight={600} mb={2}>
                Deleted Data Summary
              </Typography>
              
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: theme.palette.action.hover,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    User Profile
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {deletionData.user ? "✓ Deleted" : "✗ Not Found"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: theme.palette.action.hover,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Profile Data
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {deletionData.profile ? "✓ Deleted" : "✗ Not Found"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: theme.palette.action.hover,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Pictures
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {deletionData.pictures || 0}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: theme.palette.action.hover,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Chats
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {deletionData.chats || 0}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: theme.palette.action.hover,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Matches
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {deletionData.matches || 0}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: theme.palette.action.hover,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Blocks
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {deletionData.blocks || 0}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: theme.palette.action.hover,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Reports
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {deletionData.reports || 0}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: theme.palette.action.hover,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Photo Verifications
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {deletionData.photoVerifications || 0}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: theme.palette.action.hover,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Discovery Candidates
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {deletionData.discoveryCandidates || 0}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: theme.palette.action.hover,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Discovery Interactions
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {deletionData.discoveryInteractions || 0}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: theme.palette.action.hover,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Image Ratings
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {deletionData.imageRatings || 0}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: theme.palette.action.hover,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Validations
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {deletionData.validations || 0}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: theme.palette.action.hover,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Support Tickets
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {deletionData.support || 0}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: theme.palette.action.hover,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Khatuna Status
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {deletionData.khatuna ? "Yes" : "No"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowDeletionModal(false)}
            variant="contained"
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DeletionRequestsComponent;
